import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, Code, LayoutGrid, ShieldCheck, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCurrentRole } from "@/hooks/use-permission";
import { canAccessAdmin } from "@/lib/permissions";

const APPLICATIONS = [
  {
    id: "enterprise-wallet",
    label: "Enterprise Wallet",
    meta: "Operations, wallets, transfers, approvals",
    href: "/home",
    icon: Wallet,
    match: (path: string) => !path.startsWith("/admin") && !path.startsWith("/developer"),
    requiredPermission: undefined as string | undefined,
  },
  {
    id: "admin-portal",
    label: "Admin Portal",
    meta: "Users, configuration, deployment",
    href: "/admin/users",
    icon: ShieldCheck,
    match: (path: string) => path.startsWith("/admin"),
    requiredPermission: "admin:access" as const,
  },
  {
    id: "developer",
    label: "Developer",
    meta: "API keys and webhooks",
    href: "/developer",
    icon: Code,
    match: (path: string) => path.startsWith("/developer"),
    requiredPermission: undefined as string | undefined,
  },
];

export default function ApplicationSwitcher() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const pathname = useLocation().pathname ?? "";
  const navigate = useNavigate();
  const role = useCurrentRole();

  const visibleApplications = APPLICATIONS.filter((app) =>
    app.requiredPermission ? canAccessAdmin(role) : true
  );

  const currentApplication = visibleApplications.find((app) => app.match(pathname)) ?? visibleApplications[0];

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    function onPointerDown(e: MouseEvent | TouchEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close]);

  function handleSelect(href: string) {
    close();
    navigate(href);
  }

  // `min-w-0` on the wrapper and the trigger keeps this a shrinking flex
  // sibling of the search pill: without it the trigger refuses to go below its
  // label's width, so at 1024–1440 the label ran into the pill instead of
  // ellipsising (the `truncate` below only works inside a min-w-0 chain).
  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Application · ${currentApplication?.label ?? "Switch application"}`}
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 min-w-0 items-center gap-1.5 rounded-md px-1.5 text-left transition-colors hover:bg-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
      >
        <span className="hidden min-w-0 items-center gap-1.5 text-sm font-semibold text-ink md:flex">
          <span className="truncate">{currentApplication?.label ?? "Applications"}</span>
        </span>
        <ChevronDown aria-hidden className={cn("size-4 shrink-0 text-muted transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Switch application"
          className="absolute left-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-line bg-surface shadow"
        >
          <div className="border-b border-line px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
              <LayoutGrid aria-hidden className="size-3.5" />
              Applications
            </div>
          </div>
          <div className="max-h-[min(28rem,80vh)] overflow-y-auto p-1.5">
            {visibleApplications.map((app) => {
              const Icon = app.icon;
              const active = app.id === currentApplication?.id;
              return (
                <button
                  key={app.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelect(app.href)}
                  className={cn(
                    "flex w-full items-start gap-2.5 rounded-sm px-3 py-2.5 text-left outline-none transition-colors hover:bg-bg focus-visible:ring-2 focus-visible:ring-primary-soft",
                    active && "bg-primary-soft"
                  )}
                >
                  <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                      {app.label}
                      {active && <Check aria-hidden className="size-4 shrink-0 text-primary" />}
                    </span>
                    <span className="block text-xs text-muted">{app.meta}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
